/**
 * @file Icons.jsx
 * @description SVG icon library — stroke-based, 24×24 viewBox, round caps.
 *   All icons accept: size (default 16), color (default "currentColor"), style {}.
 *   Usage: `<HomeIcon size={18} color={T.textMid} />`
 * @author Victor Carpinelli
 * @version 1.0.0
 * @module components/Icons
 */
// ─── SVG icon library — stroke-based, 24×24 viewBox, round caps ──────────────
// All icons accept: size (default 16), color (default "currentColor"), style {}
// Usage: <HomeIcon size={18} color={T.textMid} />

import React from "react";

const icon = (path, opts = {}) => {
  const { fill = "none", strokeWidth = 2, extra = null } = opts;
  return function Icon({ size = 16, color = "currentColor", style = {}, title }) {
    return (
      <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill={fill}
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
        style={{ display: "inline-block", flexShrink: 0, verticalAlign: "middle", ...style }}
        aria-hidden={!title}
        role={title ? "img" : undefined}
      >
        {title && <title>{title}</title>}
        {path}
        {extra}
      </svg>
    );
  };
};

// ── Navigation / Layout ───────────────────────────────────────────────────────
export const HomeIcon = icon(
  <><path d="M3 9.5L12 3l9 6.5V20a1 1 0 01-1 1H5a1 1 0 01-1-1V9.5z"/><polyline points="9 22 9 12 15 12 15 22"/></>
);

export const MapIcon = icon(
  <><polygon points="1 6 1 22 8 18 16 22 23 18 23 2 16 6 8 2 1 6"/><line x1="8" y1="2" x2="8" y2="18"/><line x1="16" y1="6" x2="16" y2="22"/></>
);

export const MapPinIcon = icon(
  <><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/></>
);

export const NavigationIcon = icon(
  <><polygon points="3 11 22 2 13 21 11 13 3 11"/></>
);

// ── Users / People ────────────────────────────────────────────────────────────
export const UserIcon = icon(
  <><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/><circle cx="12" cy="7" r="4"/></>
);

export const UsersIcon = icon(
  <><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87"/><path d="M16 3.13a4 4 0 010 7.75"/></>
);

export const UserCheckIcon = icon(
  <><path d="M16 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="8.5" cy="7" r="4"/><polyline points="17 11 19 13 23 9"/></>
);

export const UserStarIcon = icon(
  <><path d="M16 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="8.5" cy="7" r="4"/><path d="M20 8l.94 2.06L23 11l-2.06.94L20 14l-.94-2.06L17 11l2.06-.94L20 8z"/></>
);

// ── Communication ─────────────────────────────────────────────────────────────
export const BellIcon = icon(
  <><path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 01-3.46 0"/></>
);

export const BellOffIcon = icon(
  <><path d="M13.73 21a2 2 0 01-3.46 0"/><path d="M18.63 13A17.89 17.89 0 0118 8"/><path d="M6.26 6.26A5.86 5.86 0 006 8c0 7-3 9-3 9h14"/><path d="M18 8a6 6 0 00-9.33-5"/><line x1="1" y1="1" x2="23" y2="23"/></>
);

export const MessageIcon = icon(
  <><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/></>
);

export const InboxIcon = icon(
  <><polyline points="22 12 16 12 14 15 10 15 8 12 2 12"/><path d="M5.45 5.11L2 12v6a2 2 0 002 2h16a2 2 0 002-2v-6l-3.45-6.89A2 2 0 0016.76 4H7.24a2 2 0 00-1.79 1.11z"/></>
);

export const MailIcon = icon(
  <><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22 6 12 13 2 6"/></>
);

export const PhoneIcon = icon(
  <><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6A19.79 19.79 0 012.12 4.18 2 2 0 014.11 2h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z"/></>
);

// ── Actions / Controls ────────────────────────────────────────────────────────
export const SearchIcon = icon(
  <><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></>
);

export const FilterIcon = icon(
  <><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/></>
);

export const MinusIcon = icon(
  <><line x1="5" y1="12" x2="19" y2="12"/></>
);

export const PlusIcon = icon(
  <><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></>
);

export const XIcon = icon(
  <><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></>
);

export const ChevronDownIcon = icon(
  <><polyline points="6 9 12 15 18 9"/></>
);

export const ChevronRightIcon = icon(
  <><polyline points="9 18 15 12 9 6"/></>
);

export const ChevronLeftIcon = icon(
  <><polyline points="15 18 9 12 15 6"/></>
);

export const ArrowRightIcon = icon(
  <><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></>
);

export const ArrowLeftIcon = icon(
  <><line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/></>
);

export const RefreshIcon = icon(
  <><polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/><path d="M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15"/></>
);

// ── Status / Feedback ─────────────────────────────────────────────────────────
export const CheckIcon = icon(
  <><polyline points="20 6 9 17 4 12"/></>
);

export const CheckCircleIcon = icon(
  <><path d="M22 11.08V12a10 10 0 11-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></>
);

export const AlertTriangleIcon = icon(
  <><path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></>
);

export const AlertCircleIcon = icon(
  <><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></>
);

export const InfoIcon = icon(
  <><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></>
);

// ── Content ───────────────────────────────────────────────────────────────────
export const ClipboardIcon = icon(
  <><path d="M16 4h2a2 2 0 012 2v14a2 2 0 01-2 2H6a2 2 0 01-2-2V6a2 2 0 012-2h2"/><rect x="8" y="2" width="8" height="4" rx="1" ry="1"/></>
);

export const FileTextIcon = icon(
  <><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/></>
);

export const BookmarkIcon = icon(
  <><path d="M19 21l-7-5-7 5V5a2 2 0 012-2h10a2 2 0 012 2z"/></>
);

export const StarIcon = icon(
  <><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></>
);

// ── Business / Work ───────────────────────────────────────────────────────────
export const BriefcaseIcon = icon(
  <><rect x="2" y="7" width="20" height="14" rx="2" ry="2"/><path d="M16 21V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v16"/></>
);

export const BuildingIcon = icon(
  <><rect x="1" y="3" width="15" height="18"/><path d="M16 8h4l3 3v9H16V8z"/><line x1="5" y1="7" x2="5" y2="7"/><line x1="5" y1="11" x2="5" y2="11"/><line x1="10" y1="7" x2="10" y2="7"/><line x1="10" y1="11" x2="10" y2="11"/></>
);

export const GlobeIcon = icon(
  <><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z"/></>
);

export const TrendingUpIcon = icon(
  <><polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/><polyline points="17 6 23 6 23 12"/></>
);

export const DollarIcon = icon(
  <><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6"/></>
);

// ── Settings / Admin ──────────────────────────────────────────────────────────
export const SettingsIcon = icon(
  <><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z"/></>
);

export const ShieldIcon = icon(
  <><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></>
);

export const CopyIcon = icon(
  <><rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"/></>
);

export const LockIcon = icon(
  <><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0110 0v4"/></>
);

export const KeyIcon = icon(
  <><path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 11-7.778 7.778 5.5 5.5 0 017.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4"/></>
);

export const FlagIcon = icon(
  <><path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z"/><line x1="4" y1="22" x2="4" y2="15"/></>
);

export const TrashIcon = icon(
  <><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a1 1 0 011-1h4a1 1 0 011 1v2"/></>
);

export const EditIcon = icon(
  <><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/></>
);

export const PinIcon = icon(
  <><line x1="12" y1="17" x2="12" y2="22"/><path d="M5 17h14v-1.76a2 2 0 00-1.11-1.79l-1.78-.9A2 2 0 0115 10.76V6h1a2 2 0 000-4H8a2 2 0 000 4h1v4.76a2 2 0 01-1.11 1.79l-1.78.9A2 2 0 005 15.24z"/></>
);

export const EyeIcon = icon(
  <><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></>
);
export const EyeOffIcon = icon(
  <><path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94"/><path d="M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19"/><line x1="1" y1="1" x2="23" y2="23"/></>
);

export const LogOutIcon = icon(
  <><path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></>
);

// ── Time ──────────────────────────────────────────────────────────────────────
export const ClockIcon = icon(
  <><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></>
);

export const CalendarIcon = icon(
  <><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></>
);

// ── Service settings (used in SETTING_ICONS replacement) ─────────────────────
// These are purpose-built 20px icons for setting chips
export function SettingIcon({ setting, size = 14, color = "currentColor" }) {
  const props = { size, color };
  switch (setting) {
    case "Home":      return <HomeIcon {...props} />;
    case "School":    return <SchoolIcon {...props} />;
    case "Clinic":    return <ClinicIcon {...props} />;
    case "Community": return <TreeIcon {...props} />;
    case "Remote":    return <MonitorIcon {...props} />;
    default:          return null;
  }
}

export const SchoolIcon = icon(
  <><path d="M4 20V10.5L12 6l8 4.5V20"/><path d="M9 20V15h6v5"/><path d="M12 6V3"/><path d="M9 3h6"/><polyline points="4 10 12 6 20 10"/></>
);

export const ClinicIcon = icon(
  <><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M9 12h6"/><path d="M12 9v6"/></>
);

export const HeartIcon = icon(
  <><path d="M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z"/></>
);

export const TreeIcon = icon(
  <><path d="M12 22V13"/><path d="M20 9H4l4-6h8l4 6z"/><path d="M16 13H8l2-4h4l2 4z"/></>
);

export const MonitorIcon = icon(
  <><rect x="2" y="3" width="20" height="14" rx="2" ry="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/></>
);

// ── Misc ──────────────────────────────────────────────────────────────────────
export const SparkleIcon = icon(
  <><path d="M12 3v1m0 16v1M4.22 4.22l.7.7m12.16 12.16l.7.7M3 12h1m16 0h1M4.22 19.78l.7-.7M18.36 5.64l.7-.7"/><circle cx="12" cy="12" r="3"/></>
);

export const LayersIcon = icon(
  <><polygon points="12 2 2 7 12 12 22 7 12 2"/><polyline points="2 17 12 22 22 17"/><polyline points="2 12 12 17 22 12"/></>
);

export const AwardIcon = icon(
  <><circle cx="12" cy="8" r="7"/><polyline points="8.21 13.89 7 23 12 20 17 23 15.79 13.88"/></>
);

export const TargetIcon = icon(
  <><circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="6"/><circle cx="12" cy="12" r="2"/></>
);

export const MegaphoneIcon = icon(
  <><path d="M3 11l18-5v12L3 14v-3z"/><path d="M11.6 16.8a3 3 0 11-5.8-1.6"/></>
);

export const CoinsIcon = icon(
  <><circle cx="8" cy="8" r="6"/><path d="M18.09 10.37A6 6 0 1110.34 18"/><path d="M7 6h1v4"/><path d="M16.71 13.88l.7.71-2.82 2.82"/></>
);

export const ZapIcon = icon(
  <><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></>
);

export const PauseIcon = icon(
  <><rect x="6" y="4" width="4" height="16"/><rect x="14" y="4" width="4" height="16"/></>
);

export const VideoIcon = icon(
  <><polygon points="23 7 16 12 23 17 23 7"/><rect x="1" y="5" width="15" height="14" rx="2" ry="2"/></>
);

export const ExternalLinkIcon = icon(
  <><path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></>
);

export const DownloadIcon = icon(
  <><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></>
);

export const PlayIcon = icon(
  <><polygon points="5 3 19 12 5 21 5 3"/></>
);

export const CreditCardIcon = icon(
  <><rect x="1" y="4" width="22" height="16" rx="2" ry="2"/><line x1="1" y1="10" x2="23" y2="10"/></>
);

export const CarIcon = icon(
  <><path d="M19 17h2l.64-2.54a1 1 0 00-.61-1.2L19 12.28V10a1 1 0 00-1-1h-3l-1.5-3H10.5L9 9H6a1 1 0 00-1 1v2.28l-2.03.98a1 1 0 00-.61 1.2L3 17h2"/><circle cx="7.5" cy="17" r="2"/><circle cx="16.5" cy="17" r="2"/><path d="M9.5 17h5"/></>
);

export const BusIcon = icon(
  <><path d="M4 16V8a4 4 0 014-4h8a4 4 0 014 4v8"/><path d="M4 11h16"/><path d="M4 16h16"/><circle cx="7.5" cy="18.5" r="1.5"/><circle cx="16.5" cy="18.5" r="1.5"/><path d="M9 7h6"/></>
);

export const MoonIcon = icon(
  <><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/></>
);

export const ShareIcon = icon(
  <><path d="M4 12v8a2 2 0 002 2h12a2 2 0 002-2v-8"/><polyline points="16 6 12 2 8 6"/><line x1="12" y1="2" x2="12" y2="15"/></>
);

export const NoteIcon = icon(
  <><path d="M13 20l7-7"/><path d="M13 20v-6a1 1 0 011-1h6V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2h7"/></>
);

// ─── Rank tier + trend icons ──────────────────────────────────────────────
// Cross-surface — rank receipt, Pulse Attention Stack, Performance per-case
// table, Agency case map cards. Self-contained (don't share `icon()` helper)
// because they have multi-shape composition + a custom viewBox.
//
// tier:  'top' | 'strong' | 'above-median' | 'below-median' | 'stale' | null
//        null suppresses the icon (UI hides it when cohort < min or no coords).
// trend: 'rising' | 'falling' | 'steady' | null
//        null suppresses (insufficient snapshot history). 'steady' also
//        suppresses — don't crowd the UI with no-change indicators.
//
// Stale renders a square (not arrows) — freshness staleness is the primary
// action signal regardless of search position. Renew is free, fix in one
// click. Override of the percentile-derived tier.

const TIER_COLORS = {
  top:            "#15803D",
  strong:         "#16A34A",
  "above-median": "#65A30D",
  "below-median": "#94A3B8",
  stale:          "#D97706",
};

// Warm hairline used for the empty slots of the strength meter — gives the
// lit bars a constant "out of 4" scale to read against.
const TIER_TRACK = "#D8D2C7";

const TIER_LABELS = {
  top:            "Top",
  strong:         "Strong",
  "above-median": "Above median",
  "below-median": "Below median",
  stale:          "Stale: needs renewal",
};

export function RankTierIcon({ tier, size = 18, title, ...rest }) {
  if (!tier) return null;
  const color = TIER_COLORS[tier] || "#94A3B8";
  const tip = title || TIER_LABELS[tier] || `Rank tier: ${tier}`;

  if (tier === "stale") {
    return (
      <svg width={size} height={size} viewBox="0 0 24 24"
           role="img" aria-label={tip} {...rest}>
        <title>{tip}</title>
        <rect x="5" y="5" width="14" height="14" rx="2"
              fill={color} fillOpacity="0.18" stroke={color} strokeWidth="2" />
      </svg>
    );
  }

  // Fixed 4-bar signal meter (▮▮▯▯). The tier lights the lowest N bars in its
  // own color against a faint track; ascending bar height makes the level
  // legible even at 12px. Deliberately NOT a chevron/arrow shape: tier here is
  // LEVEL (where the case stands), and the only arrow in the row is the
  // separate trend modifier (direction). Mixing chevrons for both made an
  // "above-median but falling" case read as up-and-down at once.
  const SLOTS = 4;
  const filled =
    tier === "top"          ? 4 :
    tier === "strong"       ? 3 :
    tier === "above-median" ? 2 : 1;

  const BAR_W = 3.6, GAP = 1.7, X0 = 3, BASE_Y = 22;
  const bars = [];
  for (let i = 0; i < SLOTS; i++) {
    const h = 7 + i * 4;                 // 7, 11, 15, 19 — ascending
    const x = X0 + i * (BAR_W + GAP);
    const lit = i < filled;              // light the shortest N bars (signal-style)
    bars.push(
      <rect key={i} x={x} y={BASE_Y - h} width={BAR_W} height={h} rx={1.2}
        fill={lit ? color : TIER_TRACK} />
    );
  }
  return (
    <svg width={size} height={size} viewBox="0 0 24 24"
         role="img" aria-label={tip} {...rest}>
      <title>{tip}</title>
      {bars}
    </svg>
  );
}

export function RankTrendModifier({ trend, size = 12, ...rest }) {
  if (!trend || trend === "steady") return null;
  const isUp = trend === "rising";
  const color = isUp ? "#2563EB" : "#DC2626";  // blue rising, red falling
  const label = isUp ? "Rising vs last week" : "Falling vs last week";

  return (
    <svg width={size} height={size} viewBox="0 0 16 16"
         role="img" aria-label={label} {...rest}>
      {isUp
        ? <polyline points="3,11 8,5 13,11"
                    fill="none" stroke={color} strokeWidth="2.5"
                    strokeLinecap="round" strokeLinejoin="round" />
        : <polyline points="3,5 8,11 13,5"
                    fill="none" stroke={color} strokeWidth="2.5"
                    strokeLinecap="round" strokeLinejoin="round" />}
    </svg>
  );
}

// Convenience wrapper — tier icon + trend modifier side-by-side. Most
// surfaces want both, this keeps the call site minimal.
export function RankTierBadge({ tier, trend, size = 18, gap = 4, style, title }) {
  if (!tier) return null;
  return (
    <span title={title}
          style={{
            display: "inline-flex", alignItems: "center", gap, lineHeight: 1,
            ...style,
          }}>
      <RankTierIcon tier={tier} size={size} />
      <RankTrendModifier trend={trend} size={Math.max(10, size - 6)} />
    </span>
  );
}
